import {
	ClickAwayListener,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { Info } from "lucide-react";
import { type FC, type ReactElement, useState } from "react";
import { isFilledValue } from "web_myplann/Shared/Utils/isFilledValue";

type Props = {
	label: string;
	value?: string | number | null | undefined;
	valueTooltip?: string;
	element?: ReactElement;
	hideIfEmpty?: boolean;
};

export const DetailRow: FC<Props> = ({
	label,
	value = "",
	valueTooltip,
	element,
	hideIfEmpty = true,
}) => {
	const theme = useTheme();
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);

	if (hideIfEmpty && !isFilledValue(value) && !element) {
		return null;
	}

	return (
		<Stack
			direction="row"
			alignItems="baseline"
			px={theme.spacingTokens.lg}
			py={theme.spacingTokens.md}
			sx={{
				borderBottom: theme.colors.border.solid(theme.palette.divider),
				"&:last-of-type": { borderBottom: "none" },
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				mr={theme.spacingTokens.xl}
				flex="0 1 auto"
				maxWidth="50%"
				minWidth={0}
			>
				<Typography variant="body2">{label}</Typography>
			</Stack>

			<Stack
				direction="row"
				alignItems="baseline"
				gap={theme.spacingTokens.xs}
				justifyContent="flex-end"
				flex="1 1 0%"
				minWidth={0}
			>
				{!element && (
					<Typography
						variant="body2"
						color={theme.palette.grey[900]}
						tabular
						flex={1}
						minWidth={0}
						textAlign="right"
						whiteSpace="normal"
						sx={{ wordBreak: "break-word" }}
					>
						{value}
					</Typography>
				)}
				{element}

				{valueTooltip && (
					<ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
						<Tooltip
							open={isTooltipOpen}
							disableTouchListener
							disableFocusListener
							disableHoverListener
							placement="top-end"
							title={valueTooltip}
							slotProps={{
								tooltip: {
									sx: {
										bgcolor: theme.palette.common.white,
										color: theme.palette.grey[900],
										boxShadow: theme.shadows[3],
										borderRadius: theme.radii.md,
										fontWeight: theme.typography.bodyStrong.fontWeight,
										fontSize: theme.typography.caption.fontSize,
									},
								},
							}}
						>
							<Info
								size={theme.iconSize.xs}
								color={theme.palette.grey[400]}
								onClick={() => setIsTooltipOpen((prev) => !prev)}
								style={{ cursor: "pointer", flexShrink: 0 }}
							/>
						</Tooltip>
					</ClickAwayListener>
				)}
			</Stack>
		</Stack>
	);
};
